import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  KlaraBrief,
  KlaraDocument,
  KlaraInputs,
  KlaraLogEntry,
  KlaraProject,
  KlaraStatus,
} from '../../model/klara';
import { klaraApi } from '../../services/klaraApi';
import { uploadFile } from '../../services/klaraUpload';
import { useAppTranslation } from '../../services/i18n';
import { PageTabs } from '../../components/PageTabs';
import { PageHeader } from '../../components/PageHeader';
import { ProjectList } from './ProjectList';
import { InputsTab } from './InputsTab';
import { BriefTab, KlaraEdits } from './BriefTab';
import { isWeak } from './components/EvaluationBand';
import { OutputsTab } from './OutputsTab';

/**
 * Statuses where the partner still owes us a callback.
 *
 * The same set answers both questions it is asked: whether to keep polling,
 * and whether the topbar carries an action — there is nothing for the user to
 * press while we are waiting on Klara. Kept as one list so the two cannot
 * drift apart.
 */
const IN_FLIGHT = [
  'brief_requested',
  'evaluation_requested',
  'generation_requested',
  'generating',
];

/**
 * Polled rather than pushed: there is no socket in this app (action_cable is
 * commented out in application.rb), and the events arrive in bursts minutes
 * apart, so a slow poll is honest about the cadence without pretending to be
 * live.
 */
const POLL_INTERVAL = 3_000;

/**
 * Requesting a message answers 202 the moment the job is queued, so the status
 * is still `draft` when the response lands — and `draft` is not in flight.
 * Without covering that hand-off the poll never starts and the screen sits
 * there forever. Bounded so an abandoned draft does not poll all afternoon.
 *
 * Counted in ticks, so it has to grow when the interval shrinks: the dispatch
 * opens a case and uploads every source file before the status moves, which is
 * comfortably longer than a few seconds.
 */
const HANDOFF_TICKS = 20;

/** The four sections on the right, as the partner's optional seeds. */
const SEED_FIELDS: Array<keyof KlaraInputs> = [
  'outline',
  'key_topics',
  'learning_objectives',
  'learning_outcomes',
];

const REQUIRED_FIELDS: Array<keyof KlaraInputs> = [
  'course_name',
  'target_audience',
  'course_length_words',
  'language',
];

/**
 * Seeded with what the form displays. Without it `language` renders as Čeština
 * from a fallback while state holds nothing, and the completeness check fails
 * on a form the user can see is filled in.
 */
const DEFAULT_INPUTS: KlaraInputs = { language: 'cs' };

/**
 * Statuses a tab has its own content for. Before them it would only spin, so
 * the tab stays closed unless the user has just sent the job its way (see
 * `unlockedTabs`). A failed job keeps both open: Výstupy is where the failure
 * is shown. A cancelled one is decided in `isTabOpen`, since nothing more is
 * coming and the status alone does not say what there is to show.
 */
const TAB_STATUSES: Record<string, KlaraStatus[]> = {
  zadani: [
    'evaluation_requested',
    'evaluation_ready',
    'generation_requested',
    'generating',
    'completed',
    'failed',
  ],
  vystupy: ['generation_requested', 'generating', 'completed', 'failed'],
};

const isTabOpen = (tab: string, project: KlaraProject, unlocked: string[]) => {
  if (!TAB_STATUSES[tab]) return true;
  // Zadání kurzu can still show a finished evaluation; Výstupy would only
  // promise output that is never coming.
  if (project.status === 'cancelled') return tab === 'zadani' && !!project.evaluation;
  return TAB_STATUSES[tab].includes(project.status) || unlocked.includes(tab);
};

const unlockedTabsKey = (projectId: string) => `klara.unlockedTabs.${projectId}`;

/** Deep JSON equality ignoring key order, which jsonb does not preserve. */
const sameJson = (a: unknown, b: unknown) =>
  JSON.stringify(sortKeys(a)) === JSON.stringify(sortKeys(b));

const sortKeys = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (!value || typeof value !== 'object') return value;
  const record = value as Record<string, unknown>;
  return Object.fromEntries(
    Object.keys(record)
      .sort()
      .map((key) => [key, sortKeys(record[key])])
  );
};

/** Quiet period after the last keystroke before the draft is saved. */
const AUTOSAVE_DELAY = 1_000;

export function KlaraPage() {
  // Read from the splat rather than named params: this page is one route, so
  // /klara, /klara/:id and /klara/:id/:tab all arrive here as path segments.
  const params = useParams();
  const [projectId, tab] = (params['*'] ?? '').split('/').filter(Boolean);
  const navigate = useNavigate();

  if (!projectId) {
    return (
      <ProjectList
        onOpen={(id) => navigate(`/klara/${id}`)}
        onCreate={async () => {
          const project = await klaraApi.create({});
          navigate(`/klara/${project.id}`);
        }}
      />
    );
  }

  return <ProjectDetail key={projectId} projectId={projectId} tab={tab} />;
}

function ProjectDetail({ projectId, tab }: { projectId: string; tab?: string }) {
  const t = useAppTranslation();
  const navigate = useNavigate();
  const [project, setProject] = useState<KlaraProject | null>(null);
  const [log, setLog] = useState<KlaraLogEntry[]>([]);
  // Held here because the topbar renders the action that sends it.
  const [draftedBrief, setDraftedBrief] = useState<KlaraBrief | null>(null);
  // Brief fields the user has edited, as paths like "variables.outline". Held
  // here so the tint does not come back when the tab remounts; a reverted text
  // stays white too, because the edit happened.
  const [touchedFields, setTouchedFields] = useState<string[]>([]);
  const touchField = useCallback(
    (path: string) =>
      setTouchedFields((current) =>
        current.includes(path) ? current : [...current, path]
      ),
    []
  );
  // Same, for the revisions made on Zadání kurzu: the canonical topbar puts
  // "generovat" up here, so the page has to hold what that button sends.
  const [briefEdits, setBriefEdits] = useState<KlaraEdits>({
    research: true,
    brief: null,
  });
  // Held here because the topbar renders the action that depends on it.
  const [inputs, setInputs] = useState<KlaraInputs | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // A rejected action used to vanish into an unhandled promise: the button came
  // back and nothing said why. error_message on the project only covers the
  // failures the server got far enough to record.
  const [actionFailed, setActionFailed] = useState(false);
  // Autosave is the only way draft inputs reach the server, so a failure has
  // to be said out loud.
  const [saveFailed, setSaveFailed] = useState(false);
  const polling = useRef<ReturnType<typeof setInterval> | null>(null);
  // A poll still waiting on its answer. Without this a slow connection stacks
  // requests every three seconds, and the reply that lands last wins whether
  // or not it is the newest.
  const loading = useRef(false);
  const [handoffTicks, setHandoffTicks] = useState(0);
  // Tabs the user has sent the job towards. The status lags behind: a request
  // answers 202 on the old status, and after "Pokračovat" the brief is drafted
  // for minutes before the evaluation starts on its own. Kept in the browser,
  // because the API does not say which button started the draft.
  const [unlockedTabs, setUnlockedTabs] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(unlockedTabsKey(projectId)) ?? '[]');
    } catch {
      return [];
    }
  });
  const unlockTab = (next: string) =>
    setUnlockedTabs((current) => {
      if (current.includes(next)) return current;
      const updated = [...current, next];
      try {
        localStorage.setItem(unlockedTabsKey(projectId), JSON.stringify(updated));
      } catch {
        // Still unlocked for this visit.
      }
      return updated;
    });

  const load = useCallback(async () => {
    if (loading.current) return null;
    loading.current = true;

    try {
      const [loaded, entries] = await Promise.all([
        klaraApi.get(projectId),
        klaraApi.events(projectId),
      ]);
      setProject(loaded);
      setLog(entries);
      setLoadFailed(false);
      return loaded;
    } catch {
      setLoadFailed(true);
      return null;
    } finally {
      loading.current = false;
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  // Coming back to the tab should not mean waiting out a poll interval to find
  // out what happened while it was in the background.
  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) load();
    };

    document.addEventListener('visibilitychange', refresh);
    return () => document.removeEventListener('visibilitychange', refresh);
  }, [load]);

  // Adopted from the server on first load only: re-adopting on every poll would
  // overwrite whatever the user is typing.
  // Autosave. Read through refs so a save queued before a keystroke still
  // sends the text as it is when the save actually runs.
  const savedInputs = useRef<string | null>(null);
  const saveQueue = useRef<Promise<unknown>>(Promise.resolve());
  const inputsRef = useRef(inputs);
  inputsRef.current = inputs;
  const statusRef = useRef(project?.status);
  statusRef.current = project?.status;

  useEffect(() => {
    if (project && inputs === null) {
      setInputs({ ...DEFAULT_INPUTS, ...(project.inputs ?? {}) });
      savedInputs.current = JSON.stringify(project.inputs ?? {});
    }
  }, [project, inputs]);

  // Queued, so a slow autosave cannot land after a newer one — or after the
  // dispatch, which reads the inputs from the database.
  const persistInputs = useCallback(() => {
    const run = saveQueue.current.then(async () => {
      const current = inputsRef.current;
      if (!current || statusRef.current !== 'draft') return;
      const snapshot = JSON.stringify(current);
      if (snapshot === savedInputs.current) return;
      try {
        await klaraApi.update(projectId, current);
      } catch (e) {
        setSaveFailed(true);
        throw e;
      }
      savedInputs.current = snapshot;
      setSaveFailed(false);
    });
    // A failed save must not block the ones after it; it is retried with the
    // next change, and explicit callers still see the rejection.
    saveQueue.current = run.catch(() => undefined);
    return run;
  }, [projectId]);

  useEffect(() => {
    if (inputs === null || project?.status !== 'draft') return;
    const handle = setTimeout(
      () => persistInputs().catch(() => undefined),
      AUTOSAVE_DELAY
    );
    return () => clearTimeout(handle);
  }, [inputs, project?.status, persistInputs]);

  // Leaving the project inside the app would otherwise drop the last second of
  // typing.
  useEffect(
    () => () => {
      persistInputs().catch(() => undefined);
    },
    [persistInputs]
  );

  // Polling stops as soon as the partner owes us nothing, so a finished or
  // failed project does not keep querying forever.
  useEffect(() => {
    const inFlight =
      project &&
      (IN_FLIGHT.includes(project.status) ||
        project.transfer_status === 'running');
    // The dispatch has landed, so the hand-off window has done its job.
    if (inFlight && handoffTicks > 0) setHandoffTicks(0);

    if (!inFlight && handoffTicks === 0) {
      if (polling.current) clearInterval(polling.current);
      polling.current = null;
      return;
    }

    if (!polling.current) {
      polling.current = setInterval(() => {
        // A background tab has nobody watching it. Browsers throttle the timer
        // anyway, so the requests it does make are a poor picture of the job —
        // the visibility handler below catches the screen up on return.
        if (document.hidden) return;

        setHandoffTicks((remaining) => (remaining > 0 ? remaining - 1 : 0));
        load();
      }, POLL_INTERVAL);
    }

    return () => {
      if (polling.current) clearInterval(polling.current);
      polling.current = null;
    };
  }, [project, load, handoffTicks]);

  // Edits to Klara's brief exist only here until "Pokračovat" or "Generovat"
  // sends them: the API has no way to save them sooner. Both requests store
  // the brief before answering, so the reload that follows clears this.
  const pendingBrief =
    project?.status === 'brief_ready'
      ? draftedBrief
      : project?.status === 'evaluation_ready'
        ? briefEdits.brief
        : null;
  const unsentBrief =
    !!pendingBrief && !sameJson(pendingBrief, project?.brief ?? {});
  const [confirmLeave, setConfirmLeave] = useState(false);

  useEffect(() => {
    if (!unsentBrief) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [unsentBrief]);

  // A closed tab reached by URL would still render its spinner.
  useEffect(() => {
    if (!project || !tab || isTabOpen(tab, project, unlockedTabs)) return;
    navigate(`/klara/${project.id}`, { replace: true });
  }, [project, tab, unlockedTabs, navigate]);

  // Only when there is nothing to show. Once the project has loaded, a failed
  // poll is a blip on the connection: replacing the screen would throw away
  // whatever the user has typed, and the next poll usually recovers.
  if (loadFailed && !project) {
    return <Alert severity="error">{t('klara.detail.loadFailed')}</Alert>;
  }
  if (!project) {
    return (
      <Box sx={{ padding: '48px', textAlign: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const downloadUrl = (document: KlaraDocument) =>
    klaraApi.downloadUrl(project.id, document.id);

  // Enabled either once Klara has drafted, or — for a user who wrote the four
  // sections themselves — straight from the draft state. The second case still
  // sends message 1 first; it just does not stop to review a draft the user has
  // effectively already written.
  // Read from the live form, not from the saved project: the user has not
  // necessarily pressed save, and the button is what triggers the save.
  const liveInputs = inputs ?? project.inputs ?? {};
  const filled = (field: keyof KlaraInputs) =>
    (liveInputs[field] ?? '').toString().trim().length > 0;
  const seedsComplete = SEED_FIELDS.every(filled);
  const requiredComplete = REQUIRED_FIELDS.every(filled);
  const continueEnabled =
    project.status === 'brief_ready' ||
    (project.status === 'draft' && requiredComplete && seedsComplete);

  const saveInputs = async () => {
    await persistInputs();
  };

  const attachFile = async (file: File) => {
    const uploadedFileId = await uploadFile(file);
    await klaraApi.attachSource(project.id, uploadedFileId);
    await load();
  };

  const detachSource = async (sourceId: number) => {
    await klaraApi.detachSource(project.id, sourceId);
    await load();
  };

  // Answers whether the request got through, so the caller can stop rather
  // than unlock a tab and navigate to a screen for something that never
  // happened.
  const send = async (request: () => Promise<unknown>) => {
    setActionFailed(false);
    try {
      await request();
      return true;
    } catch {
      setActionFailed(true);
      await load();
      return false;
    }
  };

  const requestBrief = async (options: { autoEvaluate?: boolean } = {}) => {
    if (!(await send(() => klaraApi.requestBrief(project.id, options)))) return;
    // A bare "Nechat doplnit Klarou" stays on Vstupy, where the draft lands.
    if (options.autoEvaluate) unlockTab('zadani');
    setHandoffTicks(HANDOFF_TICKS);
    await load();
  };

  const requestEvaluation = async (brief: KlaraBrief) => {
    const sent = await send(() =>
      klaraApi.requestEvaluation(project.id, {
        constants: brief.constants ?? {},
        variables: brief.variables ?? {},
      })
    );
    if (!sent) return;
    unlockTab('zadani');
    setHandoffTicks(HANDOFF_TICKS);
    await load();
    navigate(`/klara/${project.id}/zadani`);
  };

  const requestGeneration = async (options: {
    research: boolean;
    brief?: KlaraBrief;
  }) => {
    if (!(await send(() => klaraApi.requestGeneration(project.id, options))))
      return;
    unlockTab('vystupy');
    setHandoffTicks(HANDOFF_TICKS);
    await load();
    navigate(`/klara/${project.id}/vystupy`);
  };

  // "Sestavit kurz" (F1). Unlike the messages above, the controller flips
  // transfer_status to 'running' synchronously before answering — so the
  // freshly reloaded project already carries it, and the polling effect
  // above picks it up with no hand-off window needed.
  const transfer = async () => {
    await send(() => klaraApi.transfer(project.id));
    await load();
  };

  return (
    // layout-fill layout-column tw-absolute is how every other React page in
    // the app fills its Angular host: the host div is content-sized, so only
    // taking the root out of flow gives a percentage height something to
    // resolve against. Same as CataloguesPage and UserGroupsPage.
    <Box className="layout-fill layout-column tw-absolute">
      <PageHeader
        // The way back to the project list, which the topbar carries in every
        // designed state and the browser's own back button does not replace —
        // the tabs are routes, so back walks through them one at a time.
        icon={
          <Tooltip title={t('klara.detail.backToList')}>
            <IconButton
              size="small"
              aria-label={t('klara.detail.backToList')}
              // Awaited, so the list does not load before the name is saved.
              // A failed save keeps the user here, next to the warning, rather
              // than walking away from edits that never arrived.
              onClick={async () => {
                try {
                  await persistInputs();
                } catch {
                  return;
                }
                if (unsentBrief) setConfirmLeave(true);
                else navigate('/klara');
              }}
              sx={{ color: 'var(--color-white)' }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
        title={`${t('klara.detail.title')}: ${
          liveInputs.course_name || t('klara.list.untitled')
        }`}
        details={t(`klara.statuses.${project.status}`, {
          defaultValue: project.status,
        })}
        action={
          project.status === 'completed' ? (
            <TransferAction project={project} onTransfer={transfer} />
          ) : project.status === 'evaluation_ready' ? (
            // The canonical topbar for this state carries "generovat" and
            // nothing else — where the continue button would otherwise sit
            // disabled, saying the draft is not ready when it plainly is.
            <GenerateAction
              weak={isWeak(project.evaluation)}
              onGenerate={() =>
                requestGeneration({
                  research: briefEdits.research,
                  brief: briefEdits.brief ?? undefined,
                })
              }
            />
          ) : (
            // The canonical topbar carries no action while the partner owes us
            // a callback — there is nothing for the user to do, and a disabled
            // button that says "nejprve nechte Klaru…" is untrue by then.
            // `brief_ready` is the deliberate exception: the taxonomy files it
            // under Zpracovávání, but it is exactly the moment the user reviews
            // the draft and continues, so hiding the button would strand them.
            !IN_FLIGHT.includes(project.status) &&
            !['completed', 'failed'].includes(project.status) && (
              <Tooltip
                title={continueEnabled ? '' : t('klara.detail.continueBlocked')}
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!continueEnabled}
                    onClick={async () => {
                      if (project.status !== 'draft') {
                        await requestEvaluation(draftedBrief ?? project.brief ?? {});
                        return;
                      }
                      // The form may be unsaved; the dispatch reads the database.
                      await saveInputs();
                      await requestBrief({ autoEvaluate: true });
                      navigate(`/klara/${project.id}/zadani`);
                    }}
                    // MUI's disabled contained style is near-black text on a
                    // near-black fill, which vanishes against this bar. Spelled
                    // out so a disabled step still reads as a step.
                    sx={{
                      whiteSpace: 'nowrap',
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(255, 255, 255, 0.16)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {t('klara.inputs.continue')}
                  </Button>
                </span>
              </Tooltip>
            )
          )
        }
      />

      <Dialog open={confirmLeave} onClose={() => setConfirmLeave(false)}>
        <DialogTitle>{t('klara.detail.leaveTitle')}</DialogTitle>
        <DialogContent sx={{ '&&': { paddingTop: '20px' } }}>
          <Typography>{t('klara.detail.leaveBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmLeave(false)}>
            {t('klara.detail.leaveCancel')}
          </Button>
          <Button variant="contained" onClick={() => navigate('/klara')}>
            {t('klara.detail.leaveProceed')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* "Jak to funguje?" belongs to Vstupy, which is the screen it explains,
          and is rendered there — the other two tabs have no such banner in the
          design. */}

      {saveFailed && project.status === 'draft' && (
        <Alert severity="error">{t('klara.detail.saveFailed')}</Alert>
      )}

      {/* The page keeps whatever it last knew, so this says the screen has
          stopped keeping up rather than pretending it is still live. */}
      {loadFailed && (
        <Alert severity="warning">{t('klara.detail.loadFailed')}</Alert>
      )}

      {actionFailed && (
        <Alert severity="error">{t('klara.detail.actionFailed')}</Alert>
      )}

      {project.stalled && (
        <Alert severity="warning">{t('klara.detail.stalled')}</Alert>
      )}

      {/* Suppressed on Výstupy, where the header gives the failed transfer its
          own treatment — the same split the dispatch failure below uses. */}
      {project.transfer_status === 'failed' && (tab ?? 'vstupy') !== 'vystupy' && (
        <Alert severity="error">{t('klara.transfer.failed')}</Alert>
      )}

      {/* Page level, because all three messages are sent from a job: the
          request answered 202 long ago, so this band is the only thing that
          ever mentions the failure — on whichever screen the user is watching.
          Our own copy: error_message holds a technical string meant for
          support, not for a customer. Suppressed on a failed job, where the
          Výstupy screen gives the terminal failure its own treatment. */}
      {project.error_message && project.status !== 'failed' && (
        <Alert severity="error">{t('klara.detail.dispatchFailed')}</Alert>
      )}

      {/* The tabs sit on a white panel, as they do in the design. Not pushed
          into PageTabs itself, which other pages share and style differently.
          Grows to fill whatever the Angular shell gives us rather than
          subtracting a measured header height, so a short tab still paints
          white to the bottom and the panel survives a taller topbar. */}
      <Box
        sx={{
          backgroundColor: 'var(--color-white)',
          flexGrow: 1,
          // The page is out of document flow, so the scroll lives here rather
          // than on the window — the page header stays put and the content
          // moves under it.
          overflow: 'auto',
          // The tab strip stays put while the content moves under it. Without
          // it the strip scrolls away and switching tabs on a long form means
          // scrolling back to the top first.
          '& > .MuiTabs-root': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'var(--color-white)',
          },
        }}
      >
      <PageTabs
        currentTab={tab ?? 'vstupy'}
        onTabChange={(next) => navigate(`/klara/${project.id}/${next}`)}
        tabs={[
          {
            tab: 'vstupy',
            label: t('klara.tabs.inputs'),
            content: () => (
              <InputsTab
                project={project}
                inputs={liveInputs}
                onInputsChange={setInputs}
                onSaveInputs={saveInputs}
                onAttachFile={attachFile}
                onDetachSource={detachSource}
                onRequestBrief={requestBrief}
                onBriefChange={setDraftedBrief}
                touchedFields={touchedFields}
                onTouchField={touchField}
              />
            ),
          },
          {
            tab: 'zadani',
            label: t('klara.tabs.brief'),
            disabled: !isTabOpen('zadani', project, unlockedTabs),
            content: () => (
              <BriefTab
                project={project}
                log={log}
                protocolUrl={downloadUrl}
                edits={briefEdits}
                onEditsChange={setBriefEdits}
                touchedFields={touchedFields}
                onTouchField={touchField}
              />
            ),
          },
          {
            tab: 'vystupy',
            label: t('klara.tabs.outputs'),
            disabled: !isTabOpen('vystupy', project, unlockedTabs),
            content: () => (
              <OutputsTab
                project={project}
                log={log}
                downloadUrl={downloadUrl}
                archiveUrl={klaraApi.archiveUrl(project.id)}
                imageArchiveUrl={klaraApi.archiveUrl(project.id, 'image')}
              />
            ),
          },
        ]}
      />
      </Box>
    </Box>
  );
}

/**
 * "Generovat" — the header action while the evaluation waits for approval.
 *
 * The weak-verdict dialog travels with the button rather than staying on the
 * tab: sources are fixed at message 1, so the only routes are to generate
 * anyway or to abandon and start over, and the question belongs next to the
 * click that raises it.
 */
function GenerateAction({
  weak,
  onGenerate,
}: {
  weak: boolean;
  onGenerate: () => Promise<void>;
}) {
  const t = useAppTranslation();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    setConfirming(false);
    try {
      await onGenerate();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        disabled={busy}
        onClick={() => (weak ? setConfirming(true) : generate())}
        sx={{
          whiteSpace: 'nowrap',
          '&.Mui-disabled': {
            backgroundColor: 'rgba(255, 255, 255, 0.16)',
            color: 'rgba(255, 255, 255, 0.5)',
          },
        }}
      >
        {t('klara.brief.generate')}
      </Button>

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>{t('klara.brief.confirmWeakTitle')}</DialogTitle>
        <DialogContent sx={{ '&&': { paddingTop: '20px' } }}>
          <Typography>{t('klara.brief.confirmWeakBody')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>
            {t('klara.brief.confirmWeakCancel')}
          </Button>
          <Button variant="contained" onClick={generate}>
            {t('klara.brief.confirmWeakProceed')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * The header action for a completed project: "Sestavit kurz" through to
 * "Otevřít kurz" — the same slot the "continue" button occupies for every
 * earlier status, never shown alongside it.
 */
function TransferAction({
  project,
  onTransfer,
}: {
  project: KlaraProject;
  onTransfer: () => Promise<void>;
}) {
  const t = useAppTranslation();

  if (project.transfer_status === 'completed') {
    // content_version_id is only absent if the transfer completed before this
    // field existed — nothing to link to, so the button is left disabled
    // rather than pointing nowhere.
    return (
      <Tooltip title={t('klara.transfer.openTooltip')}>
        <span>
          <Button
            variant="contained"
            size="small"
            href={
              project.content_version_id != null
                ? `#/editor/${project.content_version_id}`
                : undefined
            }
            disabled={project.content_version_id == null}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {t('klara.transfer.open')}
          </Button>
        </span>
      </Tooltip>
    );
  }

  const running = project.transfer_status === 'running';

  return (
    <Tooltip title={running ? '' : t('klara.transfer.buildTooltip')}>
      <span>
        <Button
          variant="contained"
          size="small"
          disabled={running}
          onClick={onTransfer}
          startIcon={
            running ? <CircularProgress size={14} color="inherit" /> : undefined
          }
          sx={{
            whiteSpace: 'nowrap',
            '&.Mui-disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.16)',
              color: 'rgba(255, 255, 255, 0.5)',
            },
          }}
        >
          {running ? t('klara.transfer.inProgress') : t('klara.transfer.build')}
        </Button>
      </span>
    </Tooltip>
  );
}
